import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Button,
  Badge,
  Spinner,
  Input,
  Stat,
  IconButton,
  Flex,
  createListCollection,
  Dialog,
} from '@chakra-ui/react';
import { Card } from '@chakra-ui/react/card';
import { Alert } from '@chakra-ui/react/alert';
import { Table } from '@chakra-ui/react/table';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@chakra-ui/react/select';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@chakra-ui/react';
import { toaster } from '../components/ui/toaster';
import { useColorModeValue } from '../hooks/useColorModeValue';
import {
  FiEye,
  FiTrash2,
  FiDownload,
  FiActivity,
  FiTrendingUp,
  FiFilter,
  FiRefreshCw,
  FiBarChart,
} from 'react-icons/fi';
import { adminAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

interface Review {
  id: number;
  user_email: string;
  policy_name: string;
  standard_name: string;
  compliance_score: number | null;
  tokens_used: number | null;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface ContentStats {
  totalReviews: number;
  activeReviews: number;
  completedToday: number;
  averageScore: number;
}

const ContentReviewsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ContentStats>({
    totalReviews: 0,
    activeReviews: 0,
    completedToday: 0,
    averageScore: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [reviewSearch, setReviewSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // UI state
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const reviewsData = await adminAPI.getReviews({ limit: 100 });
      
      setReviews(reviewsData.reviews || []);
      
      // Calculate stats
      const totalReviews = reviewsData.reviews?.length || 0;
      const activeReviews = reviewsData.reviews?.filter((r: Review) => r.status === 'pending' || r.status === 'processing').length || 0;
      const completedToday = reviewsData.reviews?.filter((r: Review) => {
        if (!r.completed_at) return false;
        const today = new Date().toDateString();
        return new Date(r.completed_at).toDateString() === today;
      }).length || 0;
      
      // Calculate average score
      const completedReviews = reviewsData.reviews?.filter((r: Review) => r.compliance_score !== null) || [];
      const averageScore = completedReviews.length > 0 
        ? completedReviews.reduce((acc: number, r: Review) => acc + (r.compliance_score || 0), 0) / completedReviews.length
        : 0;
      
      setStats({
        totalReviews,
        activeReviews,
        completedToday,
        averageScore: Math.round(averageScore * 100) / 100,
      });
      
    } catch (err: any) {
      setError(err.message || t('admin.content.contentReviews.errors.failedToLoadData'));
      toaster.create({
        title: t('admin.content.common.error'),
        description: t('admin.content.contentReviews.errors.failedToLoadAssessmentsData'),
        type: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter functions
  const filteredReviews = reviews.filter(review => {
    const matchesSearch = !reviewSearch || 
      review.policy_name.toLowerCase().includes(reviewSearch.toLowerCase()) ||
      review.standard_name.toLowerCase().includes(reviewSearch.toLowerCase()) ||
      review.user_email.toLowerCase().includes(reviewSearch.toLowerCase());
    
    const matchesStatus = !statusFilter || review.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'green';
      case 'processing': return 'blue';
      case 'pending': return 'yellow';
      case 'failed': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('admin.content.common.status.completed');
      case 'processing': return t('admin.content.common.status.processing');
      case 'pending': return t('admin.content.common.status.pending');
      case 'failed': return t('admin.content.common.status.failed');
      default: return status;
    }
  };

  const handleDeleteReview = async (reviewId: number) => {
    if (!confirm(t('admin.content.contentReviews.confirmDeleteAssessment'))) return;
    
    try {
      await adminAPI.deleteReview(reviewId);
      toaster.create({
        title: t('admin.content.contentReviews.toasts.assessmentDeletedTitle'),
        description: t('admin.content.contentReviews.toasts.assessmentDeletedDescription'),
        type: 'success',
        duration: 3000,
      });
      await loadData();
    } catch (error) {
      toaster.create({
        title: t('admin.content.common.deleteFailed'),
        description: t('admin.content.contentReviews.toasts.deleteAssessmentFailedDescription'),
        type: 'error',
        duration: 3000,
      });
    }
  };

  const handleDownloadReport = async (reviewId: number) => {
    try {
      // Call the backend API to generate and download the report
      const response = await adminAPI.downloadReviewReport(reviewId);
      
      // Create a blob and download the file
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `assessment-report-${reviewId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toaster.create({
        title: t('admin.content.contentReviews.toasts.reportDownloadedTitle'),
        description: t('admin.content.contentReviews.toasts.reportDownloadedDescription'),
        type: 'success',
        duration: 3000,
      });
    } catch (error: any) {
      let description = t('admin.content.contentReviews.toasts.downloadFailedDescription');
      const errorData = error?.response?.data;
      if (errorData instanceof Blob) {
        try {
          const parsed = JSON.parse(await errorData.text());
          description = parsed?.detail || parsed?.message || description;
        } catch {
          description = error?.message || description;
        }
      } else {
        description = errorData?.detail || errorData?.message || error?.message || description;
      }
      toaster.create({
        title: t('admin.content.contentReviews.toasts.downloadFailedTitle'),
        description,
        type: 'error',
        duration: 3000,
      });
    }
  };

  if (loading) {
    return (
      <Box p={6}>
        <Flex justify="center" align="center" h="400px">
          <VStack gap={4}>
            <Spinner size="xl" color="blue.500" />
            <Text>{t('admin.content.contentReviews.loadingAssessments')}</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Title>{error}</Alert.Title>
        </Alert.Root>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Box>
            <Heading size="lg">{t('admin.content.contentReviews.title')}</Heading>
            <Text color="gray.600">{t('admin.content.contentReviews.subtitle')}</Text>
          </Box>
          <Button onClick={loadData} variant="outline">
            <FiRefreshCw />
            {t('admin.content.common.refresh')}
          </Button>
        </Flex>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 2, md: 3 }} gap={6}>
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack gap={2}>
                    <FiActivity color="orange.500" />
                    <Text>{t('admin.content.contentReviews.stats.totalAssessments')}</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText color="orange.600">{stats.totalReviews}</Stat.ValueText>
                <Stat.HelpText>{t('admin.content.contentReviews.stats.allAssessments')}</Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack gap={2}>
                    <FiTrendingUp color="green.500" />
                    <Text>{t('admin.content.contentReviews.stats.completedToday')}</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText color="green.600">{stats.completedToday}</Stat.ValueText>
                <Stat.HelpText>{t('admin.content.contentReviews.stats.assessmentsFinished')}</Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label>
                  <HStack gap={2}>
                    <FiBarChart color="purple.500" />
                    <Text>{t('admin.content.contentReviews.stats.averageScore')}</Text>
                  </HStack>
                </Stat.Label>
                <Stat.ValueText color="purple.600">{stats.averageScore.toFixed(1)}%</Stat.ValueText>
                <Stat.HelpText>{t('admin.content.contentReviews.stats.complianceRate')}</Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Reviews Section */}
        <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
          <Card.Header>
            <Flex justify="space-between" align="center">
              <Heading size="md">{t('admin.content.contentReviews.complianceAssessments')}</Heading>
              <HStack gap={2}>
                <Text fontSize="sm" color="gray.500">
                  {t('admin.content.contentReviews.avgScore', { score: stats.averageScore.toFixed(1) })}
                </Text>
                <Button colorScheme="purple" size="sm" onClick={() => setIsAnalyticsModalOpen(true)}>
                  <FiBarChart />
                  {t('admin.content.contentReviews.viewAnalytics')}
                </Button>
              </HStack>
            </Flex>
          </Card.Header>
          <Card.Body>
            <VStack gap={4} align="stretch">
              {/* Filters */}
              <HStack gap={4}>
                <Input
                  maxW="300px"
                  placeholder={t('admin.content.contentReviews.searchPlaceholder')}
                  value={reviewSearch}
                  onChange={(e) => setReviewSearch(e.target.value)}
                />
                
                <SelectRoot
                  collection={createListCollection({ items: [
                    { label: t('admin.content.common.allStatus'), value: '' },
                    { label: t('admin.content.common.status.pending'), value: 'pending' },
                    { label: t('admin.content.common.status.processing'), value: 'processing' },
                    { label: t('admin.content.common.status.completed'), value: 'completed' },
                    { label: t('admin.content.common.status.failed'), value: 'failed' }
                  ] })}
                  value={statusFilter ? [statusFilter] : []}
                  onValueChange={(e) => setStatusFilter(e.value[0] || '')}
                  size="sm"
                  maxW="200px"
                >
                  <SelectTrigger>
                    <SelectValueText placeholder={t('admin.content.common.allStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    {['', 'pending', 'processing', 'completed', 'failed'].map((status) => (
                      <SelectItem key={status} item={status}>
                        {status === '' ? t('admin.content.common.allStatus') : getStatusText(status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
                
                <Button
                  variant="outline"
                  onClick={() => {
                    setReviewSearch('');
                    setStatusFilter('');
                  }}
                >
                  <FiFilter />
                  {t('admin.content.common.clear')}
                </Button>
              </HStack>

              {/* Reviews Table */}
              <Box overflowX="auto">
                <Table.Root variant="line" size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.document')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.standard')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.user')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.status')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.score')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.tokensUsed')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.created')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.completed')}</Table.ColumnHeader>
                      <Table.ColumnHeader>{t('admin.content.contentReviews.table.actions')}</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {filteredReviews.map((review) => (
                      <Table.Row key={review.id}>
                        <Table.Cell>
                          <Text fontWeight="medium">{review.policy_name}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">{review.standard_name}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Text fontSize="sm">{review.user_email}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge colorScheme={getStatusColor(review.status)} variant="subtle">
                            {getStatusText(review.status)}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          {review.compliance_score !== null ? (
                            <Text fontSize="sm" fontWeight="medium">
                              {(review.compliance_score).toFixed(1)}%
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.500">-</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {review.tokens_used !== null ? (
                            <Text fontSize="sm" fontWeight="medium">
                              {review.tokens_used.toLocaleString()}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.500">-</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm">
                              {new Date(review.created_at).toLocaleString()}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(review.created_at).toLocaleTimeString()}
                            </Text>
                          </VStack>
                        </Table.Cell>
                        <Table.Cell>
                          {review.completed_at ? (
                            <VStack align="start" gap={0}>
                              <Text fontSize="sm">
                                {new Date(review.completed_at).toLocaleString()}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {new Date(review.completed_at).toLocaleTimeString()}
                              </Text>
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">-</Text>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={2}>
                            <IconButton
                              aria-label={t('admin.content.contentReviews.actions.viewAssessment')}
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedReview(review);
                                setIsReviewModalOpen(true);
                              }}
                            >
                              <FiEye />
                            </IconButton>
                            {review.status === 'completed' && (
                              <IconButton
                                aria-label={t('admin.content.contentReviews.actions.downloadReport')}
                                size="sm"
                                variant="ghost"
                                colorScheme="green"
                                onClick={() => handleDownloadReport(review.id)}
                              >
                                <FiDownload />
                              </IconButton>
                            )}
                            <IconButton
                              aria-label={t('admin.content.contentReviews.actions.deleteAssessment')}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDeleteReview(review.id)}
                            >
                              <FiTrash2 />
                            </IconButton>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
                
                {filteredReviews.length === 0 && (
                  <Flex justify="center" py={8}>
                    <Text color="gray.500">{t('admin.content.contentReviews.emptyNoAssessments')}</Text>
                  </Flex>
                )}
              </Box>
            </VStack>
          </Card.Body>
        </Card.Root>
      </VStack>

      {/* Review Details Modal */}
      <DialogRoot open={isReviewModalOpen} onOpenChange={(e) => setIsReviewModalOpen(e.open)} size="lg" placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <Dialog.Positioner>
          <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.content.contentReviews.details.title')}</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody>
            {selectedReview && (
              <VStack gap={4} align="stretch">
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.document')}</Text>
                  <Text>{selectedReview.policy_name}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.standard')}</Text>
                  <Text>{selectedReview.standard_name}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.user')}</Text>
                  <Text>{selectedReview.user_email}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.status')}</Text>
                  <Badge colorScheme={getStatusColor(selectedReview.status)}>
                    {getStatusText(selectedReview.status)}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.complianceScore')}</Text>
                  <Text>
                    {selectedReview.compliance_score !== null 
                      ? `${selectedReview.compliance_score.toFixed(1)}%`
                      : t('admin.content.common.notAvailable')
                    }
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.tokensUsed')}</Text>
                  <Text>
                    {selectedReview.tokens_used !== null
                      ? selectedReview.tokens_used.toLocaleString()
                      : t('admin.content.common.notAvailable')
                    }
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text fontWeight="semibold">{t('admin.content.contentReviews.details.created')}</Text>
                  <Text>{new Date(selectedReview.created_at).toLocaleString()}</Text>
                </HStack>
                {selectedReview.completed_at && (
                  <HStack justify="space-between">
                    <Text fontWeight="semibold">{t('admin.content.contentReviews.details.completed')}</Text>
                    <Text>{new Date(selectedReview.completed_at).toLocaleString()}</Text>
                  </HStack>
                )}
              </VStack>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsReviewModalOpen(false)}>
              {t('admin.content.common.close')}
            </Button>
            {selectedReview?.status === 'completed' && (
              <Button 
                colorScheme="green"
                onClick={() => handleDownloadReport(selectedReview.id)}
              >
                <FiDownload />
                {t('admin.content.contentReviews.actions.downloadReport')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
        </Dialog.Positioner>
      </DialogRoot>

      {/* Analytics Modal */}
      <DialogRoot open={isAnalyticsModalOpen} onOpenChange={(e) => setIsAnalyticsModalOpen(e.open)} size="lg" placement="center" scrollBehavior="inside">
        <DialogBackdrop />
        <Dialog.Positioner>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.content.contentReviews.analytics.title')}</DialogTitle>
            </DialogHeader>
            <DialogCloseTrigger />
            <DialogBody>
              <VStack gap={5} align="stretch">
                <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
                  <Stat.Root>
                    <Stat.Label>{t('admin.content.contentReviews.analytics.total')}</Stat.Label>
                    <Stat.ValueText color="orange.600">{stats.totalReviews}</Stat.ValueText>
                  </Stat.Root>
                  <Stat.Root>
                    <Stat.Label>{t('admin.content.contentReviews.analytics.active')}</Stat.Label>
                    <Stat.ValueText color="blue.600">{stats.activeReviews}</Stat.ValueText>
                  </Stat.Root>
                  <Stat.Root>
                    <Stat.Label>{t('admin.content.contentReviews.stats.completedToday')}</Stat.Label>
                    <Stat.ValueText color="green.600">{stats.completedToday}</Stat.ValueText>
                  </Stat.Root>
                  <Stat.Root>
                    <Stat.Label>{t('admin.content.contentReviews.analytics.avgScore')}</Stat.Label>
                    <Stat.ValueText color="purple.600">{stats.averageScore.toFixed(1)}%</Stat.ValueText>
                  </Stat.Root>
                </SimpleGrid>

                <Box>
                  <Heading size="sm" mb={2}>{t('admin.content.contentReviews.analytics.statusBreakdown')}</Heading>
                  <VStack gap={2} align="stretch">
                    {Object.entries(
                      reviews.reduce((acc: Record<string, number>, r) => {
                        acc[r.status] = (acc[r.status] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([status, count]) => (
                      <HStack key={status} justify="space-between">
                        <Badge colorScheme={getStatusColor(status)}>{getStatusText(status)}</Badge>
                        <Text fontWeight="medium">{count}</Text>
                      </HStack>
                    ))}
                    {reviews.length === 0 && <Text color="gray.500" fontSize="sm">{t('admin.content.common.noData')}</Text>}
                  </VStack>
                </Box>

                <Box>
                  <Heading size="sm" mb={2}>{t('admin.content.contentReviews.analytics.assessmentsByStandard')}</Heading>
                  <VStack gap={2} align="stretch">
                    {Object.entries(
                      reviews.reduce((acc: Record<string, number>, r) => {
                        const key = r.standard_name || t('admin.content.common.unknown');
                        acc[key] = (acc[key] || 0) + 1;
                        return acc;
                      }, {})
                    )
                      .sort((a, b) => b[1] - a[1])
                      .map(([standard, count]) => (
                        <HStack key={standard} justify="space-between">
                          <Text fontSize="sm">{standard}</Text>
                          <Text fontWeight="medium">{count}</Text>
                        </HStack>
                      ))}
                    {reviews.length === 0 && <Text color="gray.500" fontSize="sm">{t('admin.content.common.noData')}</Text>}
                  </VStack>
                </Box>

                <Box>
                  <Heading size="sm" mb={2}>{t('admin.content.contentReviews.analytics.tokenUsage')}</Heading>
                  <HStack justify="space-between">
                    <Text fontSize="sm">{t('admin.content.contentReviews.analytics.totalTokensUsed')}</Text>
                    <Text fontWeight="medium">
                      {reviews.reduce((acc, r) => acc + (r.tokens_used || 0), 0).toLocaleString()}
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsAnalyticsModalOpen(false)}>
                {t('admin.content.common.close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog.Positioner>
      </DialogRoot>
    </Box>
  );
};

export default ContentReviewsManagement; 